import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users, ClipboardList, Inbox, Share2, DollarSign, AlertTriangle, Clock,
  ChevronRight, ArrowUpRight, ArrowDownRight, Trophy, Dumbbell, MapPin,
  CheckCircle2, Cake, Package, Shield, Plus, Activity, Building2, FileWarning,
  ExternalLink, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentNoticeBanner from '@/components/PaymentNoticeBanner';

const API_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

// ── helpers ─────────────────────────────────────────────────────────────────
const formatCurrency = (amt) =>
  new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', minimumFractionDigits: 0 }).format(amt || 0);

const formatDate = (s) => {
  if (!s) return '-';
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' });
};

const daysFromNow = (s) => {
  if (!s) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const [y, m, d] = s.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  return Math.ceil((target - t) / 86400000);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { token, user, canManageFinance } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    players: [],
    tracking: [],
    pendingForms: [],
    transactions: [],
    materialSummary: null,
    financeSummary: null,
    signingAlerts: { alerts: [], summary: { total: 0, urgent: 0, warning: 0 } },
    teamsCount: 0,
  });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const safeFetch = async (url) => {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) return null;
          return await res.json();
        } catch (e) {
          return null;
        }
      };

      const [
        playersRes,
        trackingRes,
        pendingRes,
        txRes,
        matSummary,
        finSummary,
        signingsRes,
        teamsCatalog,
      ] = await Promise.all([
        safeFetch(`${API_URL}/api/players?limit=500&scope=all`),
        safeFetch(`${API_URL}/api/tracking?limit=50`),
        safeFetch(`${API_URL}/api/public/pending-players`),
        safeFetch(`${API_URL}/api/finance/transactions?limit=10`),
        safeFetch(`${API_URL}/api/materials/summary`),
        safeFetch(`${API_URL}/api/finance/summary?period=month`),
        safeFetch(`${API_URL}/api/signings/alerts`),
        safeFetch(`${API_URL}/api/catalogs/teams`),
      ]);

      const players = playersRes?.data || playersRes || [];
      const tracking = trackingRes || [];
      const pendingForms = pendingRes?.players || pendingRes?.data || pendingRes || [];
      const transactions = txRes?.data || txRes || [];

      setData({
        players: Array.isArray(players) ? players : [],
        tracking: Array.isArray(tracking) ? tracking : [],
        pendingForms: Array.isArray(pendingForms) ? pendingForms : [],
        transactions: Array.isArray(transactions) ? transactions : [],
        materialSummary: matSummary || { total_quantity: 0, total_items: 0, low_stock_items: [] },
        financeSummary: finSummary || { total_income: 0, total_expense: 0, balance: 0 },
        signingAlerts: signingsRes || { alerts: [], summary: { total: 0, urgent: 0, warning: 0 } },
        teamsCount: Array.isArray(teamsCatalog) ? teamsCatalog.length : 0,
      });
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  // ─── Métricas derivadas ────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const { players, tracking, pendingForms, materialSummary, teamsCount } = data;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(today.getTime() - 7 * 86400000);
    const thirtyDays = new Date(today.getTime() + 30 * 86400000);

    const activePlayers = players.filter((p) => p.status === 'active').length;
    const trialPlayers = players.filter((p) => p.status === 'trial').length;
    const fromForm = players.filter((p) => p.imported_from === 'formulario').length;

    const recentTracking = tracking.filter((t) => {
      if (!t.date) return false;
      const [y, m, d] = t.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= sevenDaysAgo;
    });

    // Equipos donde tenemos representados (distinct origin_club)
    const teamsWithPlayers = new Set(players.filter((p) => p.origin_club).map((p) => p.origin_club));

    // Alertas
    const expiredCerts = [];
    const expiringCerts = [];
    const contractAlerts = [];
    players.forEach((p) => {
      ['health_certificate_expiry', 'fitness_certificate_expiry'].forEach((field) => {
        if (!p[field]) return;
        const d = new Date(p[field]);
        const item = {
          ...p,
          certType: field === 'health_certificate_expiry' ? 'Carnet Salud' : 'Aptitud Física',
          expiryDate: p[field],
        };
        if (d < today) expiredCerts.push(item);
        else if (d <= thirtyDays) expiringCerts.push(item);
      });
      ['contract_club_expiry', 'contract_representation_expiry'].forEach((field) => {
        if (!p[field]) return;
        const d = new Date(p[field]);
        const item = {
          ...p,
          contractType: field === 'contract_club_expiry' ? 'club' : 'representation',
          expiryDate: p[field],
          status: d < today ? 'expired' : 'expiring',
        };
        if (d < today) contractAlerts.push(item);
        else if (d <= thirtyDays) contractAlerts.push(item);
      });
    });

    const totalAlerts =
      expiredCerts.length + expiringCerts.length + contractAlerts.length +
      (data.signingAlerts?.summary?.total || 0) +
      (materialSummary?.low_stock_items?.length || 0);

    return {
      activePlayers,
      trialPlayers,
      fromForm,
      recentTrackingCount: recentTracking.length,
      teamsWithPlayers: teamsWithPlayers.size,
      teamsCount,
      expiredCerts,
      expiringCerts,
      contractAlerts,
      totalAlerts,
    };
  }, [data]);

  const copyPublicFormLink = async () => {
    const url = `${window.location.origin}/formulario`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link del formulario copiado');
    } catch {
      toast.error('No se pudo copiar — link: ' + url);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600" />
        </div>
      </Layout>
    );
  }

  const { players, tracking, pendingForms, transactions, materialSummary, financeSummary, signingAlerts } = data;

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="dashboard">
        <PaymentNoticeBanner />
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Hola{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-sm text-gray-500">
              {new Date().toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <Badge variant="outline" className="self-start sm:self-end">
            <Activity className="w-3 h-3 mr-1" />
            {players.length} jugadores · {metrics.teamsWithPlayers} equipos
          </Badge>
        </div>

        {/* ── Quick Actions ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <QuickAction
            icon={Users}
            label="Ver jugadores"
            onClick={() => navigate('/players')}
            testId="qa-players"
          />
          <QuickAction
            icon={ClipboardList}
            label="Nuevo seguimiento"
            onClick={() => navigate('/seguimiento/nuevo')}
            testId="qa-tracking"
            primary
          />
          <QuickAction
            icon={Inbox}
            label="Formularios"
            badge={pendingForms.length || null}
            onClick={() => navigate('/formularios-recibidos')}
            testId="qa-forms"
          />
          <QuickAction
            icon={Share2}
            label="Compartir form"
            onClick={copyPublicFormLink}
            testId="qa-share"
          />
          <QuickAction
            icon={DollarSign}
            label="Movimientos"
            onClick={() => navigate('/finance')}
            testId="qa-finance"
          />
        </div>

        {/* ── Stats Cards ───────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Representados"
            value={players.length}
            sub={`${metrics.activePlayers} activos · ${metrics.trialPlayers} en prueba`}
            icon={Users}
            color="brand"
            onClick={() => navigate('/players')}
            testId="stat-players"
          />
          <StatCard
            title="Pendientes de revisión"
            value={pendingForms.length}
            sub={pendingForms.length === 0 ? 'Todo al día' : 'Aprobá o rechazá'}
            icon={Inbox}
            color={pendingForms.length > 0 ? 'amber' : 'emerald'}
            onClick={() => navigate('/formularios-recibidos')}
            testId="stat-pending"
          />
          <StatCard
            title="Seguimientos (7d)"
            value={metrics.recentTrackingCount}
            sub={`${tracking.length} totales`}
            icon={ClipboardList}
            color="emerald"
            onClick={() => navigate('/seguimiento')}
            testId="stat-tracking"
          />
          <StatCard
            title="Equipos representados"
            value={metrics.teamsWithPlayers}
            sub={`de ${metrics.teamsCount} en catálogo`}
            icon={Building2}
            color="violet"
            onClick={() => navigate('/players')}
            testId="stat-teams"
          />
        </div>

        {/* ── Main 3 columns ────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Col 1: Últimos seguimientos */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-700">
                  <ClipboardList className="w-4 h-4 text-brand-600" />
                  Últimos seguimientos
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-brand-600" onClick={() => navigate('/seguimiento')}>
                  Ver todo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {tracking.length === 0 ? (
                <EmptyState icon={ClipboardList} text="Sin seguimientos aún" cta="Registrar uno" onClick={() => navigate('/seguimiento/nuevo')} />
              ) : (
                tracking.slice(0, 5).map((t) => {
                  const isPartido = t.event_type === 'partido';
                  const Icon = isPartido ? Trophy : Dumbbell;
                  const playersCount = (t.players_observed || []).length;
                  return (
                    <div
                      key={t.id}
                      onClick={() => navigate(`/seguimiento/${t.id}`)}
                      className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer bg-slate-50 hover:bg-brand-50 hover:shadow-sm transition-all group"
                      data-testid={`recent-tracking-${t.id}`}
                    >
                      <div className={`p-2 rounded-lg shrink-0 ${isPartido ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate group-hover:text-brand-700">
                          {t.club || 'Sin equipo'}
                          {t.opponent && <span className="text-slate-500 font-normal"> vs {t.opponent}</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDate(t.date)} · {playersCount} {playersCount === 1 ? 'jugador' : 'jugadores'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Col 2: Finanzas (solo admin / superadmin) */}
          {canManageFinance && (
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-700">
                  <DollarSign className="w-4 h-4 text-brand-600" />
                  Finanzas del mes
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-brand-600" onClick={() => navigate('/finance')}>
                  Ver todo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <p className="text-xs text-slate-400 mb-1">Balance</p>
                <p className={`text-2xl font-bold ${(financeSummary?.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatCurrency(financeSummary?.balance)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-2.5 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-1 text-emerald-700 mb-0.5">
                    <ArrowDownRight className="w-3 h-3" />
                    <span className="text-xs">Ingresos</span>
                  </div>
                  <p className="text-base font-semibold text-emerald-800">
                    {formatCurrency(financeSummary?.total_income || 0)}
                  </p>
                </div>
                <div className="p-2.5 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-1 text-red-600 mb-0.5">
                    <ArrowUpRight className="w-3 h-3" />
                    <span className="text-xs">Egresos</span>
                  </div>
                  <p className="text-base font-semibold text-red-700">
                    {formatCurrency(financeSummary?.total_expense || 0)}
                  </p>
                </div>
              </div>
              {transactions.length > 0 ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-400 font-medium">Últimos movimientos</p>
                  {transactions.slice(0, 3).map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-xs text-slate-600 truncate max-w-[150px]">{tx.description || 'Sin descripción'}</span>
                      <span className={`text-xs font-medium shrink-0 ${tx.type === 'ingreso' ? 'text-emerald-600' : 'text-red-500'}`}>
                        {tx.type === 'ingreso' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 text-center py-3">Sin movimientos recientes</p>
              )}
            </CardContent>
          </Card>
          )}

          {/* Col 3: Alertas */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-700">
                  <AlertTriangle className="w-4 h-4 text-brand-600" />
                  Alertas
                </CardTitle>
                {metrics.totalAlerts > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                    {metrics.totalAlerts}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(metrics.expiredCerts.length > 0 || metrics.expiringCerts.length > 0) && (
                <AlertGroup
                  title="Certificados médicos"
                  icon={Shield}
                  expired={metrics.expiredCerts.slice(0, 2)}
                  expiring={metrics.expiringCerts.slice(0, 2)}
                  onItemClick={(p) => navigate(`/players/${p.id}`)}
                />
              )}

              {metrics.contractAlerts.length > 0 && (
                <AlertGroup
                  title="Contratos"
                  icon={FileWarning}
                  expired={metrics.contractAlerts.filter((a) => a.status === 'expired').slice(0, 2)}
                  expiring={metrics.contractAlerts.filter((a) => a.status === 'expiring').slice(0, 2)}
                  onItemClick={(p) => navigate(`/players/${p.id}`)}
                />
              )}

              {signingAlerts?.alerts?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                    <Cake className="w-3 h-3" /> Sub-13 a fichar
                  </p>
                  <div className="space-y-1.5">
                    {signingAlerts.alerts.slice(0, 3).map((a, i) => (
                      <div
                        key={i}
                        onClick={() => navigate('/signings')}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:shadow-sm transition-all ${
                          a.alert_level === 'urgent' ? 'bg-red-50 hover:bg-red-100' : 'bg-amber-50 hover:bg-amber-100'
                        }`}
                      >
                        <span className="text-xs text-slate-700 truncate">{a.player_name}</span>
                        <Badge className={`text-xs h-5 ${a.alert_level === 'urgent' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                          {a.days_until_13}d
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(materialSummary?.low_stock_items?.length || 0) > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Stock bajo
                  </p>
                  <div className="space-y-1.5">
                    {materialSummary.low_stock_items.slice(0, 3).map((item, i) => (
                      <div
                        key={i}
                        onClick={() => navigate('/investments?tab=inventory')}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-brand-50 transition-all"
                      >
                        <span className="text-xs text-slate-700 truncate">{item.name}</span>
                        <span className="text-xs font-semibold text-amber-600">
                          {item.quantity}/{item.min_stock}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {metrics.totalAlerts === 0 && (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm font-medium text-slate-700">Todo en orden</p>
                  <p className="text-xs text-slate-400">Sin alertas activas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Info de origen y formulario público ─────────────────── */}
        <Card className="border-slate-200 bg-gradient-to-r from-brand-50/50 via-white to-emerald-50/30">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-brand-100 rounded-xl">
                  <Share2 className="w-6 h-6 text-brand-700" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Captación vía formulario público</h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    <span className="font-bold text-brand-700">{metrics.fromForm}</span> jugadores ingresaron por el formulario de padres
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyPublicFormLink} data-testid="copy-form-link">
                  <Copy className="w-4 h-4 mr-1.5" /> Copiar link
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open('/formulario', '_blank')} data-testid="open-form">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Ver formulario
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────
const QuickAction = ({ icon: Icon, label, onClick, testId, badge, primary }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all duration-200 group ${
      primary
        ? 'bg-brand-600 hover:bg-brand-700 border-brand-600 text-white shadow-md hover:shadow-lg'
        : 'bg-white hover:bg-brand-50 border-slate-200 text-slate-700 hover:border-brand-200 hover:shadow-md'
    }`}
  >
    <Icon className={`w-6 h-6 transition-transform group-hover:scale-110 ${primary ? 'text-white' : 'text-brand-600'}`} />
    <span className="text-sm font-medium text-center">{label}</span>
    {badge && (
      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
        {badge}
      </span>
    )}
  </button>
);

const StatCard = ({ title, value, sub, icon: Icon, color, onClick, testId }) => {
  const colorMap = {
    brand: { bg: 'bg-brand-50', icon: 'text-brand-600', accent: 'text-brand-700' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', accent: 'text-amber-700' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', accent: 'text-emerald-700' },
    violet: { bg: 'bg-violet-50', icon: 'text-violet-600', accent: 'text-violet-700' },
  };
  const c = colorMap[color] || colorMap.brand;
  return (
    <Card
      onClick={onClick}
      data-testid={testId}
      className="cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-slate-200 group"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-2.5 rounded-lg ${c.bg} group-hover:scale-110 transition-transform`}>
            <Icon className={`w-5 h-5 ${c.icon}`} />
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
        </div>
        <p className="text-3xl font-bold text-slate-800">{value}</p>
        <p className="text-sm text-slate-500 font-medium mt-0.5">{title}</p>
        {sub && <p className={`text-xs mt-1.5 ${c.accent}`}>{sub}</p>}
      </CardContent>
    </Card>
  );
};

const AlertGroup = ({ title, icon: Icon, expired, expiring, onItemClick }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
      <Icon className="w-3 h-3" /> {title}
    </p>
    <div className="space-y-1.5">
      {expired.map((a, i) => (
        <div
          key={`exp-${i}`}
          onClick={() => onItemClick(a)}
          className="flex items-center justify-between p-2 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 hover:shadow-sm transition-all"
        >
          <span className="text-xs text-slate-700 truncate">
            {a.first_name} {a.last_name}
          </span>
          <Badge variant="destructive" className="text-xs h-5">Vencido</Badge>
        </div>
      ))}
      {expiring.map((a, i) => (
        <div
          key={`expg-${i}`}
          onClick={() => onItemClick(a)}
          className="flex items-center justify-between p-2 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 hover:shadow-sm transition-all"
        >
          <span className="text-xs text-slate-700 truncate">
            {a.first_name} {a.last_name}
          </span>
          <Badge className="bg-amber-100 text-amber-700 text-xs h-5">
            {(() => {
              const d = daysFromNow(a.expiryDate);
              return d != null ? `${d}d` : 'pronto';
            })()}
          </Badge>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, text, cta, onClick }) => (
  <div className="text-center py-8">
    <Icon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
    <p className="text-sm text-slate-500 mb-3">{text}</p>
    {cta && (
      <Button size="sm" variant="outline" onClick={onClick}>
        <Plus className="w-3.5 h-3.5 mr-1" /> {cta}
      </Button>
    )}
  </div>
);

export default Dashboard;
